import { PortalRoleRanks, ProjectRoleRanks } from "./types/layout/types.js";
import { CUSTOM_ROLE_RANK, defaultPortalRoleRanks, defaultProjectRoleRanks } from "./roles.js";
import _ from "lodash";

export const folderPermissions = () => {
  return {
    RESOURCE_UPLOAD: {
      id: 1,
      description: "RESOURCE_UPLOAD",
      type: "RESOURCE_TYPE_FOLDER",
      status: "ACTIVE",
    },
    RESOURCE_DOWNLOAD: {
      id: 2,
      description: "RESOURCE_DOWNLOAD",
      type: "RESOURCE_TYPE_FOLDER",
      status: "ACTIVE",
    },
    RESOURCE_COMMENT: {
      id: 3,
      description: "RESOURCE_COMMENT",
      type: "RESOURCE_TYPE_FOLDER",
      status: "ACTIVE",
    },
    RESOURCE_SHARE: {
      id: 4,
      description: "RESOURCE_SHARE",
      type: "RESOURCE_TYPE_FOLDER",
      status: "ACTIVE",
    },
    RESOURCE_STATUS_CHANGE: {
      id: 5,
      description: "RESOURCE_STATUS_CHANGE",
      type: "RESOURCE_TYPE_FOLDER",
      status: "ACTIVE",
    },
    RESOURCE_MODIFY: {
      id: 6,
      description: "RESOURCE_MODIFY",
      type: "RESOURCE_TYPE_FOLDER",
      status: "ACTIVE",
    },
    RESOURCE_DELETE: {
      id: 7,
      description: "RESOURCE_DELETE",
      type: "RESOURCE_TYPE_FOLDER",
      status: "ACTIVE",
    },
    RESOURCE_VIEW: {
      id: 8,
      description: "RESOURCE_VIEW",
      type: "RESOURCE_TYPE_FOLDER",
      status: "ACTIVE",
    },
    RESOURCE_CHANGE_PERMISSION: {
      id: 9,
      description: "RESOURCE_CHANGE_PERMISSION",
      type: "RESOURCE_TYPE_FOLDER",
      status: "ACTIVE",
    },
  };
};

const {
  RESOURCE_UPLOAD,
  RESOURCE_DOWNLOAD,
  RESOURCE_COMMENT,
  RESOURCE_SHARE,
  RESOURCE_STATUS_CHANGE,
  RESOURCE_MODIFY,
  RESOURCE_DELETE,
  RESOURCE_VIEW,
  RESOURCE_CHANGE_PERMISSION,
} = folderPermissions();

export function folderRoles() {
  return {
    RESOURCE_ADMIN: {
      id: 1,
      frontendDisplayIndex: 1,
      description: "Can edit, change permissions and transmit to other spaces",
      frontendName: "Full Access",
      code: "FULL_ACCESS",
      type: "RESOURCE_TYPE_FOLDER",
      permissions: [
        RESOURCE_CHANGE_PERMISSION.description,
        RESOURCE_VIEW.description,
        RESOURCE_UPLOAD.description,
        RESOURCE_STATUS_CHANGE.description,
        RESOURCE_DOWNLOAD.description,
        RESOURCE_COMMENT.description,
        RESOURCE_SHARE.description,
        RESOURCE_MODIFY.description,
        RESOURCE_DELETE.description,
      ],
      permissionIds: [
        RESOURCE_VIEW.id,
        RESOURCE_CHANGE_PERMISSION.id,
        RESOURCE_UPLOAD.id,
        RESOURCE_STATUS_CHANGE.id,
        RESOURCE_DOWNLOAD.id,
        RESOURCE_COMMENT.id,
        RESOURCE_SHARE.id,
        RESOURCE_MODIFY.id,
        RESOURCE_DELETE.id,
      ],
    },
    RESOURCE_EDITOR: {
      id: 2,
      frontendDisplayIndex: 3,
      description: "Can create, upload, download & modify",
      frontendName: "Can Edit",
      code: "CAN_EDIT",
      type: "RESOURCE_TYPE_FOLDER",
      permissions: [
        RESOURCE_VIEW.description,
        RESOURCE_STATUS_CHANGE.description,
        RESOURCE_DOWNLOAD.description,
        RESOURCE_COMMENT.description,
        RESOURCE_MODIFY.description,
      ],
      permissionIds: [
        RESOURCE_VIEW.id,
        RESOURCE_STATUS_CHANGE.id,
        RESOURCE_DOWNLOAD.id,
        RESOURCE_COMMENT.id,
        RESOURCE_MODIFY.id,
      ],
    },
    RESOURCE_COMMENTER: {
      id: 3,
      frontendDisplayIndex: 4,
      description: "Can comment, change status, download & view",
      frontendName: "Can Comment & Change Status",
      code: "CAN_COMMENT",
      type: "RESOURCE_TYPE_FOLDER",
      permissions: [
        RESOURCE_VIEW.description,
        RESOURCE_STATUS_CHANGE.description,
        RESOURCE_DOWNLOAD.description,
        RESOURCE_COMMENT.description,
      ],
      permissionIds: [
        RESOURCE_VIEW.id,
        RESOURCE_DOWNLOAD.id,
        RESOURCE_COMMENT.id,
        RESOURCE_STATUS_CHANGE.id,
      ],
    },
    RESOURCE_DOWNLOADER: {
      id: 4,
      frontendDisplayIndex: 5,
      description: "Can download & view",
      frontendName: "Can Download",
      code: "CAN_DOWNLOAD",
      type: "RESOURCE_TYPE_FOLDER",
      permissions: [RESOURCE_VIEW.description, RESOURCE_DOWNLOAD.description],
      permissionIds: [RESOURCE_VIEW.id, RESOURCE_DOWNLOAD.id],
    },
    RESOURCE_CREATOR: {
      id: 5,
      frontendDisplayIndex: 2,
      description: "Can upload or create any entities",
      frontendName: "Can create",
      code: "CAN_CREATE",
      type: "RESOURCE_TYPE_FOLDER",
      permissions: [
        RESOURCE_VIEW.description,
        RESOURCE_UPLOAD.description,
        RESOURCE_STATUS_CHANGE.description,
        RESOURCE_DOWNLOAD.description,
        RESOURCE_COMMENT.description,
        RESOURCE_MODIFY.description,
      ],
      permissionIds: [
        RESOURCE_VIEW.id,
        RESOURCE_UPLOAD.id,
        RESOURCE_STATUS_CHANGE.id,
        RESOURCE_DOWNLOAD.id,
        RESOURCE_COMMENT.id,
        RESOURCE_MODIFY.id,
      ],
    },
    RESOURCE_VIEWER: {
      id: 6,
      frontendDisplayIndex: 6,
      description: "Cannot edit, download or change permissions",
      frontendName: "Can View",
      code: "CAN_VIEW",
      type: "RESOURCE_TYPE_FOLDER",
      permissions: [RESOURCE_VIEW.description],
      permissionIds: [RESOURCE_VIEW.id],
    },
    RESOURCE_NO_ACCESS: {
      id: 7,
      frontendDisplayIndex: 7,
      description: "Cannot view, edit, download or change permissions",
      frontendName: "No access",
      code: "NO_ACCESS",
      type: "RESOURCE_TYPE_FOLDER",
      permissions: [],
      permissionIds: [],
    },
  };
}

export const getPermissionConstants = () => {
  const permissions = folderRoles();
  const obj: any = {};

  Object.values(permissions).forEach((role) => {
    obj[role.code] = role.id;
  });

  return obj;
};

export function getFolderRolesArray() {
  const folderRolesData = folderRoles();
  return (
    Object.keys(folderRolesData) as Array<keyof typeof folderRolesData>
  ).map((key) => ({
    ...folderRolesData[key],
    name: key,
  }));
}

export const MAX_FILE_UPLOAD_SIZE = 10 * 1024 * 1024 * 1024; // 10 GB
export const MAX_EXCEL_IMPORT_CONTAINERS = 10000;

/**
 * Product rule for a file answered into a form instance (a `fileUpload` /
 * `image` answer, or a field attachment). Checked in the browser before the
 * upload starts, so the user is told immediately.
 *
 * ADVISORY, not authoritative: the server does not enforce this number today.
 * The cap it does enforce is MAX_FORM_FILE_UPLOAD_SIZE below, so a file between
 * the two sizes is accepted by the API and stopped only by the client.
 */
export const MAX_FORM_ANSWER_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

/**
 * Multer's cap on that same upload — a heap/DoS guard, NOT the product rule,
 * which is why it sits deliberately ABOVE MAX_FORM_ANSWER_FILE_SIZE. The
 * in-place image editor re-encodes to a data URL with no second size check, so
 * capping the transport at 10 MB would newly reject an edit that grew past the
 * limit, which the base64 route it replaced always accepted. Making the two
 * numbers equal is a product decision, not a cleanup.
 */
export const MAX_FORM_FILE_UPLOAD_SIZE = 25 * 1024 * 1024; // 25 MB
export const MAX_REQUEST_BODY_SIZE = 1024 * 1024 * 1024; // 1 GB

export const DOCUMENT_NUMBER_FIELD_ALIASES = [
  "documentnumber",
  "documentno",
  "docnumber",
  "docno",
];
export const normalizeCustomFieldName = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9]/g, "");
export const isDocumentNumberField = (name?: string | null): boolean =>
  !!name &&
  DOCUMENT_NUMBER_FIELD_ALIASES.includes(normalizeCustomFieldName(name));

export const getFileExtension = (fileName?: string | null): string => {
  const value = String(fileName ?? "").trim();
  const dotIndex = value.lastIndexOf(".");
  return dotIndex > 0 ? value.slice(dotIndex + 1).toLowerCase() : "";
};
export const extractDocumentNumber = (
  row?: Record<string, unknown> | null,
): string | undefined => {
  if (!row) return undefined;
  const key = Object.keys(row).find((k) => isDocumentNumberField(k));
  if (!key) return undefined;
  const value = String(row[key] ?? "").trim();
  return value || undefined;
};
export const DEFAULT_IMAGE_DISPLAY_WIDTH = 30;

/**
 * Master list of file types a portal admin may allow for upload, keyed by
 * UPPERCASE extension. The EXTENSION is the identity; the MIME value is the
 * canonical Content-Type for that extension.
 *
 * The values are deliberately NOT unique — JPG/JPEG share image/jpeg, and
 * DWG/DXF/RVT/NWD/NWC share application/octet-stream because browsers report
 * that for any binary format they cannot identify. Never build a MIME ->
 * extension reverse lookup from this map.
 *
 * Stored per portal at `metaData.ALLOWED_FILE_TYPES` as a subset of this map;
 * `null`/absent means "no restriction". Enforced server-side in
 * fileUpload.service.ts (fileFilter) and streamUpload.service.ts
 * (isFileTypeAllowed), and validated against in updateAllowedFileTypes.
 */
export const SUPPORTED_FILE_TYPES_MASTER_LIST: Record<string, string> = {
  PDF: "application/pdf",
  DOC: "application/msword",
  DOCX: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  XLS: "application/vnd.ms-excel",
  XLSX: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  PPT: "application/vnd.ms-powerpoint",
  PPTX: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  CSV: "text/csv",
  TXT: "text/plain",
  JPG: "image/jpeg",
  JPEG: "image/jpeg",
  PNG: "image/png",
  GIF: "image/gif",
  SVG: "image/svg+xml",
  WEBP: "image/webp",
  DWG: "application/octet-stream",
  DXF: "application/octet-stream",
  IFC: "application/x-step",
  RVT: "application/octet-stream",
  NWD: "application/octet-stream",
  NWC: "application/octet-stream",
  ZIP: "application/zip",
  MP4: "video/mp4",
};
export const buildAllowedExtensionsAccept = (
  allowed?: Record<string, string> | null,
): string =>
  allowed
    ? Object.keys(allowed)
        .map((extension) => `.${extension.toLowerCase()}`)
        .join(",")
    : "";

export const isAllowedFileType = (
  fileName?: string | null,
  allowed?: Record<string, string> | null,
): boolean => {
  if (!allowed) return true;
  const extension = getFileExtension(fileName).toUpperCase();
  return extension !== "" && allowed[extension] !== undefined;
};

export const RESOURCE_TYPE_PROJECT = "RESOURCE_TYPE_PROJECT";
export const RESOURCE_TYPE_PORTAL = "RESOURCE_TYPE_PORTAL";
export const RESOURCE_TYPE_TRANSMITTAL = "RESOURCE_TYPE_TRANSMITTAL";
export const RESOURCE_TYPE_SPACE = "RESOURCE_TYPE_SPACE";
export const PROJECT_DASHBOARD_RESOURCE_TYPE = "PROJECT_DASHBOARD";
export const PROJECT_DASHBOARD_SELECTED_LAYOUT_KEY =
  "PROJECT_DASHBOARD_SELECTED_LAYOUT";

export const PERMISSION_GROUP_IDS = {
  // PORTAL groups (1-99)
  ORGANISATIONS: 1,
  PORTAL_ROLES: 2,
  STATUSES: 3,
  PORTAL_USERS: 4,
  PROJECTS_IN_PORTAL: 5,
  PORTAL_GENERAL: 6,
  SUBSCRIPTION: 7,
  COSTDATABASE: 8,
  CHARGES: 9,
  UNITS: 10,
  CURRENCIES: 11,
  ORG_UNIT_TYPES: 12,
  CUSTOM_MODULES: 13,
  FORM_TEMPLATES: 14,
  REVIEW_FLOWS_CHECKLIST: 15,
  REVISION_SERIES: 16,
  PORTAL_GROUPS: 17,
  ACCESS_TEMPLATES: 18,
  // PROJECT groups (100+)
  PROJECT_USERS: 101,
  PROJECT_SPACES: 102,
  PROJECT_ROLES: 103,
  PROJECT_CONFIGS: 104,
  PROJECT_TRANSMITTALS: 105,
  PROJECT_GENERAL: 106,
  PROJECT_SPECIFIC: 107,
} as const;

export const getPermissionGroups = (t?: any) => {
  if (!t) {
    t = (args: string) => args;
  }
  return [
    // PORTAL groups
    {
      id: PERMISSION_GROUP_IDS.ORGANISATIONS,
      key: "companies",
      header: t("Organisation Permissions"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.PORTAL_ROLES,
      key: "roles",
      header: t("Role Permissions"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.STATUSES,
      key: "statuses",
      header: t("Status Permissions"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.PORTAL_USERS,
      key: "users",
      header: t("User Permissions"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.PROJECTS_IN_PORTAL,
      key: "projects",
      header: t("Project Permissions"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.PORTAL_GENERAL,
      key: "general",
      header: t("General Permission"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.SUBSCRIPTION,
      key: "subscription",
      header: t("Subscription Permission"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.COSTDATABASE,
      key: "costdatabase",
      header: t("Cost Database Permission"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.CHARGES,
      key: "charges",
      header: t("Charges Permission"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.UNITS,
      key: "units",
      header: t("Units Permission"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.CURRENCIES,
      key: "currency",
      header: t("Currencies Permission"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.ORG_UNIT_TYPES,
      key: "orgUnitTypes",
      header: t("Org Unit Types Permission"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.CUSTOM_MODULES,
      key: "customModules",
      header: t("Custom Modules Permission"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.FORM_TEMPLATES,
      key: "formTemplates",
      header: t("Form Templates Permission"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.REVIEW_FLOWS_CHECKLIST,
      key: "reviewFlowsChecklist",
      header: t("Review Flows Checklist Permission"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.REVISION_SERIES,
      key: "revisionSeries",
      header: t("Revision Series Permission"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.PORTAL_GROUPS,
      key: "groups",
      header: t("Groups Permission"),
      type: RESOURCE_TYPE_PORTAL,
    },
    {
      id: PERMISSION_GROUP_IDS.ACCESS_TEMPLATES,
      key: "accessTemplates",
      header: t("Access Templates Permission"),
      type: RESOURCE_TYPE_PORTAL,
    },
    // PROJECT groups
    {
      id: PERMISSION_GROUP_IDS.PROJECT_USERS,
      key: "users",
      header: t("User Permissions"),
      type: RESOURCE_TYPE_PROJECT,
    },
    {
      id: PERMISSION_GROUP_IDS.PROJECT_SPACES,
      key: "spaces",
      header: t("Space Permissions"),
      type: RESOURCE_TYPE_PROJECT,
    },
    {
      id: PERMISSION_GROUP_IDS.PROJECT_ROLES,
      key: "roles",
      header: t("Role Permissions"),
      type: RESOURCE_TYPE_PROJECT,
    },
    {
      id: PERMISSION_GROUP_IDS.PROJECT_CONFIGS,
      key: "configs",
      header: t("Configuration Permissions"),
      type: RESOURCE_TYPE_PROJECT,
    },
    {
      id: PERMISSION_GROUP_IDS.PROJECT_TRANSMITTALS,
      key: "transmittals",
      header: t("Transmittal Permissions"),
      type: RESOURCE_TYPE_PROJECT,
    },
    {
      id: PERMISSION_GROUP_IDS.PROJECT_GENERAL,
      key: "general",
      header: t("General Permission"),
      type: RESOURCE_TYPE_PROJECT,
    },
    {
      id: PERMISSION_GROUP_IDS.PROJECT_SPECIFIC,
      key: "specific",
      header: t("Specific Permission"),
      type: RESOURCE_TYPE_PROJECT,
    },
  ];
};

export const getPermissions = (t?: any) => {
  if (!t) {
    t = (args: string) => args;
  }
  return {
    USERS_ADD_TO_PROJECT: {
      description: t("Add users to current project"),
      id: 0,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_USERS,
    },
    USERS_REMOVE_FROM_PROJECT: {
      description: t("Remove users from a project"),
      id: 1,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_USERS,
    },
    SPACES_ADD_SPACE_USERS: {
      description: t("Add User to a space"),
      id: 2,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_SPACES,
    },
    SPACES_REMOVE_SPACE_USERS: {
      description: t("Remove User from a Space"),
      id: 3,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_SPACES,
    },
    USERS_VIEW_IN_PROJECT: {
      description: t("View Project Users"),
      id: 4,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_USERS,
    },
    EDIT_PROJECT_INFO: {
      description: t("Edit project information"),
      id: 5,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_GENERAL,
    },
    SPACES_ADD_TO_PROJECT: {
      description: t("Add project spaces"),
      id: 6,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_SPACES,
    },
    SPACES_DELETE_FROM_PROJECT: {
      description: t("Delete project spaces"),
      id: 7,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_SPACES,
    },
    SPACES_EDIT: {
      description: t("Edit Project spaces"),
      id: 8,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_SPACES,
    },
    SPACES_VIEW_IN_PROJECT: {
      description: t("View Project Spaces"),
      id: 9,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_SPACES,
    },
    TRANSMITTAL_CREATE: {
      description: t("Create Transmittal"),
      id: 10,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_TRANSMITTALS,
    },
    SPACES_ROOT_PERMISSION_CHANGE: {
      description: t("Change default Permission of Users in Space"),
      id: 11,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_SPACES,
    },
    ROLES_VIEW_IN_PROJECT: {
      description: t("View Project Roles"),
      id: 12,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_ROLES,
    },
    ROLES_ADD_TO_PROJECT: {
      description: t("Add Project Roles"),
      id: 13,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_ROLES,
    },
    ROLES_EDIT_IN_PROJECT: {
      description: t("Edit Project Roles"),
      id: 14,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_ROLES,
    },
    ROLES_CHANGE_OF_USER_IN_PROJECT: {
      description: t("Change Project role of a user"),
      id: 15,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_ROLES,
    },
    ROLES_DELETE_IN_PROJECT: {
      description: t("Delete Project Roles"),
      id: 16,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_ROLES,
    },
    CONFIGURATION_PROJECT_REMOVE_FORM_TEMPLATES: {
      description: t("Remove Form Templates From Project"),
      id: 17,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_CONFIGS,
    },
    CONFIGURATION_PROJECT_VIEW_LIST_OF_FORM_TEMPLATES: {
      description: t("View Form Templates Added In Project"),
      id: 18,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_CONFIGS,
    },
    // SPACE_CHANGE_SPACE_INFORMATION: {
    //   description: t("Change Space Information"),
    //   id: 19,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_SPACE,
    // },
    // SPACE_ADD_USERS_TO_SPACE: {
    //   description: t("Add Users to Space"),
    //   id: 20,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_SPACE,
    // },
    // SPACE_REMOVE_USERS_FROM_SPACE: {
    //   description: t("Remove users from space"),
    //   id: 21,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_SPACE,
    // },
    // SPACE_ADD_ORGANISATIONS_TO_SPACE: {
    //   description: t("Add companies to space"),
    //   id: 22,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_SPACE,
    // },
    // SPACE_REMOVE_ORGANISATIONS_FROM_SPACE: {
    //   description: t("Remove companies from space"),
    //   id: 23,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_SPACE,
    // },
    // SPACE_CREATE_FOLDERS_SUBFOLDERS: {
    //   description: t("Create folders and sub folders"),
    //   id: 24,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_SPACE,
    // },
    // SPACE_UPLOAD_FILES: {
    //   description: t("Upload files"),
    //   id: 25,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_SPACE,
    // },
    // SPACE_DOWNLOAD_FILES_FOLDERS: {
    //   description: t("Download Files/Folders"),
    //   id: 26,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_SPACE,
    // },
    // SPACE_CREATE_FILE_PLACEHOLDERS: {
    //   description: t("Create File Spaceholders"),
    //   id: 27,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_SPACE,
    // },
    // SPACE_COMMENT_ON_FILE: {
    //   description: t("Comment on a file"),
    //   id: 28,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_SPACE,
    // },
    // SPACE_DELETE_OWN_COMMENTS: {
    //   description: t("Delete own comments"),
    //   id: 29,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_SPACE,
    // },
    // SPACE_DELETE_OTHER_COMMENTS: {
    //   description: t("Delete other comments"),
    //   id: 30,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_SPACE,
    // },
    // SPACE_ASSIGN_SHARING_TO_FILE_FOLDER: {
    //   description: t("Assign sharing process to files/folder"),
    //   id: 31,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_SPACE,
    // },
    // SPACE_UNASSIGN_SHARING_TO_FILE_FOLDER: {
    //   description: t("Unassign sharing process to files/folder"),
    //   id: 32,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_SPACE,
    // },
    PROJECT_ADD_TO_PORTAL: {
      description: t("Add projects to the portal"),
      id: 33,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PROJECTS_IN_PORTAL,
    },
    PROJECT_LIST_VIEW_IN_PORTAL: {
      description: t("View all Projects List of Portal"),
      id: 34,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PROJECTS_IN_PORTAL,
    },
    PROJECT_ARCHIVE_OF_PORTAL: {
      description: t("Archive projects of the portal"),
      id: 35,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PROJECTS_IN_PORTAL,
    },
    // BELOW PERMISSION DEPRECATED

    // PROJECT_INFORMATION_EDIT: {
    //   description: t("Edit Project Information"),
    //   id: 36,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_PORTAL,
    // },
    VIEW_ACCEPTED_ORGANISATIONS: {
      description: t("View Organisations"),
      id: 37,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ORGANISATIONS,
    },
    // VIEW_INVITED_ORGANISATIONS: {
    //   description: t("View Invited Companies"),
    //   id: 38,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_PORTAL,
    // },
    PORTAL_REMOVE_ORGANISATIONS: {
      description: t("Delete Organisations"),
      id: 39,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ORGANISATIONS,
    },
    PORTAL_ADD_ORGANISATIONS: {
      description: t("Add Organisations"),
      id: 40,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ORGANISATIONS,
    },
    // ACCEPT_ORGANISATIONS: {
    //   description: t("Accept Company"),
    //   id: 41,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_PORTAL,
    // },
    // REJECT_ORGANISATIONS: {
    //   description: t("Reject Company"),
    //   id: 42,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_PORTAL,
    // },
    EDIT_INFORMATION_OF_ORGANISATIONS: {
      description: t("Edit Organisations"),
      id: 43,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ORGANISATIONS,
    },
    // ASSIGN_PROJECT_TO_REGION: {
    //   description: t("Assign projects to a region"),
    //   id: 44,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_PORTAL,
    // },
    // REMOVE_PROJECT_FROM_REGION: {
    //   description: t("Remove projects from a region"),
    //   id: 45,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_PORTAL,
    // },
    // DELETE_REGION: {
    //   description: t("Delete regions"),
    //   id: 46,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_PORTAL,
    // },
    // CREATE_REGION: {
    //   description: t("Create regions"),
    //   id: 47,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_PORTAL,
    // },
    // EDIT_REGION: {
    //   description: t("Edit regions"),
    //   id: 48,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_PORTAL,
    // },
    // VIEW_LIST_OF_REGION: {
    //   description: t("View regions"),
    //   id: 49,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_PORTAL,
    // },
    CREATE_PORTAL_ROLES: {
      description: t("Create Portal Role"),
      id: 50,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_ROLES,
    },
    DELETE_PORTAL_ROLES: {
      description: t("Delete Portal Role"),
      id: 51,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_ROLES,
    },
    VIEW_LIST_OF_PORTAL_ROLES: {
      description: t("View Portal Roles"),
      id: 52,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_ROLES,
    },
    EDIT_PORTAL_ROLES: {
      description: t("Edit Portal Roles"),
      id: 53,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_ROLES,
    },
    STATUS_CREATE_OF_PORTAL_ENTITIES: {
      description: t("Create Status of Portal Entities"),
      id: 54,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.STATUSES,
    },
    STATUS_DELETE_OF_PORTAL_ENTITIES: {
      description: t("Delete Status of Portal Entities"),
      id: 55,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.STATUSES,
    },
    // STATUS_EDIT: {
    //   description: t("Edit Status"),
    //   id: 56,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_PORTAL,
    //   groupId: PERMISSION_GROUP_IDS.STATUSES,
    // },
    STATUS_VIEW: {
      description: t("View Status"),
      id: 57,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.STATUSES,
    },
    USERS_VIEW_ALL_IN_PORTAL: {
      description: t("View all users of Portal"),
      id: 58,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_USERS,
    },
    USERS_ASSIGN_ROLE_TO_PROJECTS: {
      description: t("Assign users and their role to projects"),
      id: 59,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_USERS,
    },
    USERS_REMOVE_ACCESS_TO_PROJECTS: {
      description: t("Remove which users can access which projects"),
      id: 60,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_USERS,
    },
    USERS_ROLE_CHANGE_PORTAL: {
      description: t("Change portal role of any user(s)"),
      id: 61,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_USERS,
    },
    // USERS_ROLE_CHANGE_PROJECT: {
    //   description: t(
    //     "Change project roles of users in any projects of that portal"
    //   ),
    //   id: 62,
    //   status: "ACTIVE",
    //   type: RESOURCE_TYPE_PORTAL,
    // },
    ACCESS_PORTAL_SETTINGS: {
      description: t("Access Portal Settings"),
      id: 63,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_GENERAL,
    },
    ACCESS_PROJECTS_PART_OF: {
      description: t("Access projects that they are part of"),
      id: 64,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_GENERAL,
    },
    CHANGE_PORTAL_INFORMATION: {
      description: t("Change Portal information"),
      id: 65,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_GENERAL,
    },
    SPECIFIC_CREATE_FORM_FLOW: {
      description: t("Create Form Flow"),
      id: 66,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_SPECIFIC,
    },
    USERS_ADD_TO_VISIBILITY: {
      description: t("Add users to project mailbox visibility"),
      id: 67,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_USERS,
    },
    USERS_REMOVE_FROM_VISIBILITY: {
      description: t("Remove users from project mailbox visibility"),
      id: 68,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_USERS,
    },
    CONFIGURATION_PROJECT_SELECT_FORM_TEMPLATES: {
      description: t("Select Form Templates to be Used in Project"),
      id: 69,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_CONFIGS,
    },
    SPECIFIC_ACCESS_REVIEW_SETTINGS: {
      description: t("Access Review settings"),
      id: 70,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_SPECIFIC,
    },
    PROJECT_ROLES_VIEW_IN_PORTAL: {
      description: t("View Project Roles in Portal"),
      id: 71,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PROJECTS_IN_PORTAL,
    },
    VIEW_ORG_STRUCTURE_ORGANISATIONS: {
      description: t("View Org Structures in Org Settings"),
      id: 72,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ORGANISATIONS,
    },
    EDIT_ORG_STRUCTURE_ORGANISATIONS: {
      description: t("Edit Org Structures in Org Settings"),
      id: 73,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ORGANISATIONS,
    },
    ADD_ORG_STRUCTURE_ORGANISATIONS: {
      description: t("Add Org Structures in Org Settings"),
      id: 74,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ORGANISATIONS,
    },
    DELETE_ORG_STRUCTURE_ORGANISATIONS: {
      description: t("Delete Org Structures in Org Settings"),
      id: 75,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ORGANISATIONS,
    },
    MANAGE_PORTAL_SUBSCRIPTION: {
      description: t("Manage Portal Subscription Settings"),
      id: 76,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.SUBSCRIPTION,
    },
    VIEW_PORTAL_SUBSCRIPTION: {
      description: t("View Portal Subscription Settings"),
      id: 77,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.SUBSCRIPTION,
    },
    TRANSMITTAL_FILE_VERSION_ORIGIN_SPACE: {
      description: t("External Tab - View file version in origin space"),
      id: 78,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_TRANSMITTALS,
    },
    CREATE_COSTDATABASE: {
      description: t("Create Cost Databases"),
      id: 79,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.COSTDATABASE,
    },
    DELETE_COSTDATABASE: {
      description: t("Delete Cost Databases"),
      id: 80,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.COSTDATABASE,
    },
    VIEW_LIST_OF_COSTDATABASE: {
      description: t("View Cost Databases"),
      id: 81,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.COSTDATABASE,
    },
    EDIT_COSTDATABASE: {
      description: t("Edit Cost Databases"),
      id: 82,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.COSTDATABASE,
    },
    CREATE_CHARGES: {
      description: t("Create Charges"),
      id: 83,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.CHARGES,
    },
    DELETE_CHARGES: {
      description: t("Delete Charges"),
      id: 84,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.CHARGES,
    },
    VIEW_LIST_OF_CHARGES: {
      description: t("View Charges"),
      id: 85,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.CHARGES,
    },
    EDIT_CHARGES: {
      description: t("Edit Charges"),
      id: 86,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.CHARGES,
    },
    CREATE_UNITS: {
      description: t("Create Units"),
      id: 87,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.UNITS,
    },
    DELETE_UNITS: {
      description: t("Delete Units"),
      id: 88,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.UNITS,
    },
    VIEW_LIST_OF_UNITS: {
      description: t("View Units"),
      id: 89,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.UNITS,
    },
    EDIT_UNITS: {
      description: t("Edit Units"),
      id: 90,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.UNITS,
    },
    CREATE_CURRENCIES: {
      description: t("Create Currencies"),
      id: 91,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.CURRENCIES,
    },
    DELETE_CURRENCIES: {
      description: t("Delete Currencies"),
      id: 92,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.CURRENCIES,
    },
    VIEW_LIST_OF_CURRENCIES: {
      description: t("View Currencies"),
      id: 93,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.CURRENCIES,
    },
    EDIT_CURRENCIES: {
      description: t("Edit Currencies"),
      id: 94,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.CURRENCIES,
    },
    CREATE_ORG_UNIT_TYPES: {
      description: t("Create Org Unit Types"),
      id: 95,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ORG_UNIT_TYPES,
    },
    DELETE_ORG_UNIT_TYPES: {
      description: t("Delete Org Unit Types"),
      id: 96,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ORG_UNIT_TYPES,
    },
    VIEW_LIST_OF_ORG_UNIT_TYPES: {
      description: t("View Org Unit Types"),
      id: 97,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ORG_UNIT_TYPES,
    },
    EDIT_ORG_UNIT_TYPES: {
      description: t("Edit Org Unit Types"),
      id: 98,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ORG_UNIT_TYPES,
    },
    CREATE_CUSTOM_MODULES: {
      description: t("Create Custom Modules"),
      id: 99,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.CUSTOM_MODULES,
    },
    DELETE_CUSTOM_MODULES: {
      description: t("Delete Custom Modules"),
      id: 100,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.CUSTOM_MODULES,
    },
    VIEW_LIST_OF_CUSTOM_MODULES: {
      description: t("View Custom Modules"),
      id: 101,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.CUSTOM_MODULES,
    },
    EDIT_CUSTOM_MODULES: {
      description: t("Edit Custom Modules"),
      id: 102,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.CUSTOM_MODULES,
    },
    CREATE_PROJECT_TEMPLATES: {
      description: t("Create Project Templates"),
      id: 103,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PROJECTS_IN_PORTAL,
    },
    DELETE_PROJECT_TEMPLATES: {
      description: t("Delete Project Templates"),
      id: 104,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PROJECTS_IN_PORTAL,
    },
    VIEW_LIST_OF_PROJECT_TEMPLATES: {
      description: t("View Project Templates"),
      id: 105,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PROJECTS_IN_PORTAL,
    },
    EDIT_PROJECT_TEMPLATES: {
      description: t("Edit Project Templates"),
      id: 106,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PROJECTS_IN_PORTAL,
    },
    CREATE_FORM_TEMPLATES: {
      description: t("Create Form Templates"),
      id: 107,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.FORM_TEMPLATES,
    },
    DELETE_FORM_TEMPLATES: {
      description: t("Delete Form Templates"),
      id: 108,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.FORM_TEMPLATES,
    },
    VIEW_LIST_OF_FORM_TEMPLATES: {
      description: t("View Form Templates"),
      id: 109,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.FORM_TEMPLATES,
    },
    EDIT_FORM_TEMPLATES: {
      description: t("Edit Form Templates"),
      id: 110,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.FORM_TEMPLATES,
    },
    CREATE_REVIEW_FLOWS_CHECKLIST: {
      description: t("Create Review Flows Checklist"),
      id: 111,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.REVIEW_FLOWS_CHECKLIST,
    },
    DELETE_REVIEW_FLOWS_CHECKLIST: {
      description: t("Delete Review Flows Checklist"),
      id: 112,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.REVIEW_FLOWS_CHECKLIST,
    },
    VIEW_LIST_OF_REVIEW_FLOWS_CHECKLIST: {
      description: t("View Review Flows Checklist"),
      id: 113,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.REVIEW_FLOWS_CHECKLIST,
    },
    EDIT_REVIEW_FLOWS_CHECKLIST: {
      description: t("Edit Review Flows Checklist"),
      id: 114,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.REVIEW_FLOWS_CHECKLIST,
    },
    CREATE_GROUPS: {
      description: t("Create Groups"),
      id: 115,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_GROUPS,
    },
    DELETE_GROUPS: {
      description: t("Delete Groups"),
      id: 116,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_GROUPS,
    },
    VIEW_LIST_OF_GROUPS: {
      description: t("View Groups"),
      id: 117,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_GROUPS,
    },
    EDIT_GROUPS: {
      description: t("Edit Groups"),
      id: 118,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_GROUPS,
    },
    VIEW_LIST_OF_ORG_ORGANISATIONS_USERS: {
      description: t("View list of org users"),
      id: 119,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ORGANISATIONS,
    },
    CONFIGURATION_PROJECT_SELECT_CURRENCY: {
      description: t("Select Currency To Be Used In Project"),
      id: 120,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_CONFIGS,
    },
    CONFIGURATION_VIEW_LIST_OF_LOCATIONS: {
      description: t("View List of Locations"),
      id: 121,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_CONFIGS,
    },
    CONFIGURATION_ADD_LOCATION: {
      description: t("Add locations"),
      id: 122,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_CONFIGS,
    },
    SPECIFIC_CREATE_MAILS: {
      description: t("Create Mails"),
      id: 123,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_SPECIFIC,
    },
    CONFIGURATION_EDIT_LOCATION: {
      description: t("Edit Locations"),
      id: 124,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_CONFIGS,
    },
    CONFIGURATION_DELETE_LOCATION: {
      description: t("Delete Locations"),
      id: 125,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_CONFIGS,
    },
    VIEW_LIST_OF_PROJECT_WBS_ITEMS: {
      description: t("View List of WBS Items"),
      id: 126,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_GENERAL,
    },
    USERS_VIEW_LIST_OF_TEAMS: {
      description: t("View List Of Teams"),
      id: 127,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_USERS,
    },
    USERS_ADD_TEAMS: {
      description: t("Add Teams"),
      id: 128,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_USERS,
    },
    USERS_DEACTIVATE_USERS: {
      description: t("Deactivate Users"),
      id: 129,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_USERS,
    },
    USERS_ACTIVATE_USERS: {
      description: t("Activate Users"),
      id: 130,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_USERS,
    },
    USERS_EDIT_TEAMS: {
      description: t("Edit Teams"),
      id: 131,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_USERS,
    },
    USERS_DELETE_TEAMS: {
      description: t("Delete Teams"),
      id: 132,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_USERS,
    },
    USERS_ADD_PROJECT_GROUPS: {
      description: t("Add Groups"),
      id: 133,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_USERS,
    },
    USERS_REMOVE_PROJECT_GROUPS: {
      description: t("Remove Groups"),
      id: 134,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_USERS,
    },
    USERS_VIEW_LIST_OF_PROJECT_GROUPS: {
      description: t("View List of Groups"),
      id: 135,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_USERS,
    },
    ADD_PROJECT_WBS_ITEMS: {
      description: t("Add WBS Items"),
      id: 136,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_GENERAL,
    },
    EDIT_PROJECT_WBS_ITEMS: {
      description: t("Edit WBS Items"),
      id: 137,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_GENERAL,
    },
    DELETE_PROJECT_WBS_ITEMS: {
      description: t("Delete WBS Items"),
      id: 138,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_GENERAL,
    },
    PROJECT_USERS_VIEW_IN_PORTAL: {
      description: t("View Project users in portal"),
      id: 139,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_USERS,
    },

    VIEW_LIST_OF_ACCESS_TEMPLATES: {
      description: t("View List of Access Templates"),
      id: 140,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ACCESS_TEMPLATES,
    },
    ADD_ACCESS_TEMPLATES: {
      description: t("Add Access Templates"),
      id: 141,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ACCESS_TEMPLATES,
    },
    EDIT_ACCESS_TEMPLATES: {
      description: t("Edit Access Templates"),
      id: 142,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ACCESS_TEMPLATES,
    },
    DELETE_ACCESS_TEMPLATES: {
      description: t("Delete Access Templates"),
      id: 143,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.ACCESS_TEMPLATES,
    },
    CONFIGURATION_VIEW_LIST_OF_ASSET_TYPES: {
      description: t("View List of Asset Types"),
      id: 145,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_CONFIGS,
    },
    CONFIGURATION_ADD_ASSET_TYPES: {
      description: t("Add Asset Types"),
      id: 146,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_CONFIGS,
    },
    CONFIGURATION_EDIT_ASSET_TYPES: {
      description: t("Edit Asset Types"),
      id: 147,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_CONFIGS,
    },
    CONFIGURATION_DELETE_ASSET_TYPES: {
      description: t("Delete Asset Types"),
      id: 148,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_CONFIGS,
    },
    VIEW_PROJECT_INFO: {
      description: t("View Project Information"),
      id: 149,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PROJECT,
      groupId: PERMISSION_GROUP_IDS.PROJECT_GENERAL,
    },
    USERS_ADD_TO_PORTAL: {
      description: t("Add Users to Portal"),
      id: 150,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
    },
    USERS_EDIT_OF_PORTAL: {
      description: t("Edit Portal Users"),
      id: 151,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_USERS,
    },
    CREATE_REVISION_SERIES: {
      description: t("Create Revision Series"),
      id: 152,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.REVISION_SERIES,
    },
    EDIT_REVISION_SERIES: {
      description: t("Edit Revision Series"),
      id: 153,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.REVISION_SERIES,
    },
    DELETE_REVISION_SERIES: {
      description: t("Delete Revision Series"),
      id: 154,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.REVISION_SERIES,
    },
    VIEW_LIST_OF_REVISION_SERIES: {
      description: t("View Revision Series"),
      id: 155,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.REVISION_SERIES,
    },
    RAISE_SUPPORT_TICKET: {
      description: t("Raise Support Ticket"),
      id: 156,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_GENERAL,
    },
    VIEW_SUPPORT_SLA: {
      description: t("View Support SLA"),
      id: 157,
      status: "ACTIVE",
      type: RESOURCE_TYPE_PORTAL,
      groupId: PERMISSION_GROUP_IDS.PORTAL_GENERAL,
    },
  };
};

/**
 * Every permission name, derived from {@link getPermissions} — the single source of truth.
 * Add a permission there and it appears here automatically.
 */
export type PermissionName = keyof ReturnType<typeof getPermissions>;

/**
 * The shape of {@link getPermissionNames}: a key-mirror of {@link getPermissions}, i.e. each permission
 * name maps to itself as a string LITERAL (`{ SPACES_EDIT: "SPACES_EDIT", … }`), so
 * `permissionNames.SPACES_EDIT` is still assignable everywhere a `string` is expected, while a
 * typo'd — or commented-out — permission is now a compile error instead of a silent `undefined`.
 */
export type PermissionNames = { [K in PermissionName]: K };

export const getPermissionNames = (): PermissionNames => {
  const permissions = getPermissions();
  // Accumulator is a plain string map because `Object.keys` widens each key to `string`, so TypeScript
  // cannot see that `obj[key] = key` upholds the `K -> K` correlation. The map is only complete once the
  // loop finishes, so it is narrowed once here at the end. Runtime is unchanged.
  const obj: Record<string, string> = {};

  Object.keys(permissions).forEach((key: string) => {
    obj[key] = key;
  });
  return obj as PermissionNames;
};

const transmittalsBaseColumns = [
  "indexAtProjectLevel",
  "subject",
  "from",
  "toSpaceIds",
  "createdBy",
  "createdDate",
];

const jiraBaseColumns = [
  "type",
  "jiraTicketKey",
  "summary",
  "portalName",
  "organizationName",
  "source",
  "createdBy",
  "createdOn",
  "status",
];

export const defaultFormTypes = [
  "INSPECTION",
  "PROGRESS_UPDATES",
  "BILLS",
  "CHANGE_ORDER",
  "PAYMENTS",
];

export const transmittalsDefaultViews = (t?: any) => {
  if (!t) {
    t = (args: string) => args;
  }
  return [
    {
      isShared: false,
      resourceType: "TRANSMITTALS",
      name: t("All Transmittals"),
      code: "ALL_TRANSMITTALS",
      data: {
        columns: transmittalsBaseColumns,
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: "TRANSMITTALS",
      name: t("Sent by me"),
      code: "SENT_BY_ME_TRANSMITTALS",
      data: {
        columns: transmittalsBaseColumns,
        filters: [
          {
            field: "createdById",
            operator: "equal",
            value: "CURRENT_USER",
          },
        ],
      },
      isDefault: true,
    },
  ];
};

export const assetsDefaultViews = () => {
  return [
    {
      isShared: false,
      resourceType: "ASSETS",
      name: "All Assets",
      code: "ALL_Assets",
      data: {
        columns: [
          "_checkbox_selector",
          "assetId",
          "name",
          "description",
          "type",
        ],
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: "ASSETS",
      name: "Grouped by Type",
      code: "Grouped_by_Type",
      data: {
        groupConditions: [
          {
            column: "type",
            direction: "asc",
          },
        ],
      },
      isDefault: true,
    },
  ];
};

export const jiraDefaultViews = (t?: any) => {
  if (!t) {
    t = (args: string) => args;
  }

  return [
    {
      isShared: false,
      resourceType: "JIRA",
      name: t("All Tickets"),
      code: "ALL_TICKETS",
      data: {
        columns: jiraBaseColumns,
        queryConditions: {
          createdBy: "createdByAnyPortalUsers",
        },
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: "JIRA",
      name: t("Created by me"),
      code: "CREATED_BY_ME",
      data: {
        columns: jiraBaseColumns,
        queryConditions: {
          createdBy: "createdByMe",
        },
      },
      isDefault: true,
    },
  ];
};

export const formViewDefinitions = (t?: any) => {
  if (!t) {
    t = (args: string) => args;
  }
  return [
    {
      code: "DEFAULT_VIEW",
      name: t("All Forms"),
      data: { enableSorting: true, sortConditions: [] },
    },
    {
      code: "GROUPED_BY_CREATOR",
      name: t("Grouped by Creator"),
      data: {
        groupConditions: [{ column: "createdBy.fullName", direction: "asc" }],
      },
    },
    {
      code: "GROUPED_BY_SPACE",
      name: t("Grouped by Spaces"),
      data: {
        groupConditions: [{ column: "space.spaceName", direction: "asc" }],
      },
    },
    {
      code: "GROUPED_BY_TEMPLATE",
      name: t("Grouped by Template"),
      data: {
        groupConditions: [{ column: "selectedForm.name", direction: "asc" }],
      },
    },
    {
      code: "GROUPED_BY_STATUS",
      name: t("Grouped by Status"),
      data: { groupConditions: [{ column: "status", direction: "asc" }] },
    },
  ];
};

export const FORM_LIST_GRID_TYPE = "FORM_LIST";

export const getFormsViewSet = (resourceType: any, t?: any) =>
  formViewDefinitions(t).map((view) => ({
    isShared: false,
    resourceType,
    gridType: FORM_LIST_GRID_TYPE as GridType,
    isDefault: true,
    ...view,
  }));

export const getFormsView = (resourceType: any, t?: any) =>
  getFormsViewSet(resourceType, t)[0];

export const formsDefaultViews = (t?: any) =>
  defaultFormTypes.flatMap((type) => getFormsViewSet(type, t));

export const FORM_FLOWS_GRID_TYPE = "FORM_FLOWS";

export const defaultFormFlowTypes = [
  "INSPECTION",
  "PROGRESS_UPDATES",
  "BILLS",
  "CHANGE_ORDER",
  "PAYMENTS",
];

export const GROUPED_BY_SPACE_VIEW_CODE = "GROUPED_BY_SPACE";

const identityTranslate = (key: string): string => key;

export const getFormFlowsViewSet = (resourceType: string, t: (key: string) => string = identityTranslate) => {
  return [
    {
      isShared: false,
      resourceType,
      gridType: FORM_FLOWS_GRID_TYPE as GridType,
      isDefault: true,
      code: GROUPED_BY_SPACE_VIEW_CODE,
      name: t("Grouped by Spaces"),
      data: { groupConditions: [{ column: "space.spaceName", direction: "asc" }] },
    },
    {
      isShared: false,
      resourceType,
      gridType: FORM_FLOWS_GRID_TYPE as GridType,
      isDefault: true,
      code: "CREATED_BY_ME",
      name: t("Created by Me"),
      data: { queryConditions: { where: [{ field: "createdById", operator: "equal", value: "CURRENT_USER" }] } },
    },
  ];
};

export const getFormFlowsSelectedLayoutKey = (resourceType: string) => `FORM_FLOWS_${resourceType}_SELECTED_LAYOUT`;

export const formFlowsDefaultViews = (t?: any) =>
  defaultFormFlowTypes.flatMap((type) => getFormFlowsViewSet(type, t));

export const FORM_ENTRIES_RESOURCE_TYPE_PREFIX = "FORM_ENTRIES_";

export const FORM_ENTRIES_SELECTED_LAYOUT_KEY_SUFFIX = "_SELECTED_LAYOUT";

export const FORM_ENTRIES_GRID_TYPE = "FORM_ENTRIES";

export const getFormEntriesResourceType = (templateId: any) => `${FORM_ENTRIES_RESOURCE_TYPE_PREFIX}${templateId}`;

export const getFormEntriesSelectedLayoutKey = (templateId: any) =>
  `${getFormEntriesResourceType(templateId)}${FORM_ENTRIES_SELECTED_LAYOUT_KEY_SUFFIX}`;

export const formEntriesDefaultViews = (templateId: any, t?: any) => {
  if (!t) {
    t = (args: string) => args;
  }
  return [
    {
      isShared: false,
      resourceType: getFormEntriesResourceType(templateId),
      gridType: FORM_ENTRIES_GRID_TYPE as GridType,
      isDefault: true,
      code: "DEFAULT_VIEW",
      name: t("All Entries"),
      data: { enableSorting: true, sortConditions: [] },
    },
  ];
};

export const filesDefaultViews = (t?: any) => {
  if (!t) {
    t = (args: string) => args;
  }
  return [
    {
      isShared: false,
      resourceType: "FILES",
      name: t("All Files"),
      code: "ALL_FILES",
      data: {
        enableGrouping: false,
        enableFiltering: false,
        enableAllVersions: true,
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: "FILES",
      name: t("With Review Details"),
      code: "WITH_REVIEW_DETAILS",
      data: {
        showReviewColumns: true,
        enableGrouping: false,
        enableFiltering: false,
        enableAllVersions: true,
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: "FILES",
      name: t("Unique Files Grouped"),
      code: "UNIQUE_FILES_GROUPED",
      data: {
        group: ["fileId"],
        enableGrouping: true,
        enableFiltering: true,
        enableAllVersions: true,
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: "FILES",
      name: t("Unique Files Grouped with Review Details"),
      code: "UNIQUE_FILES_GROUPED_WITH_REVIEW",
      data: {
        group: ["fileId"],
        showReviewColumns: true,
        enableGrouping: true,
        enableFiltering: true,
        enableAllVersions: true,
      },
      isDefault: true,
    },
  ];
};

export const MAX_TAG_LENGTH = 64;
export const MAX_TAGS_PER_MAIL = 50;
/**
 * Ceiling on the project-wide tag vocabulary `getMailTags` returns. The list feeds MUI Autocomplete
 * dropdowns, which are not virtualised — every option becomes a DOM node the moment the list opens —
 * so this is a browser-side budget, not just a payload one.
 */
export const MAX_PROJECT_TAG_OPTIONS = 500;

/**
 * Stable colour for a mail tag: the same text always gets the same chip colour, in the mailbox grid
 * and in both tag editors. Hash over code points so astral characters (emoji tags) do not collide on
 * a surrogate half.
 */
export const getTagColor = (tag: string): string => {
  if (!tag) return "var(--system-colour-1)";
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = (tag.codePointAt(i) ?? 0) + ((hash << 5) - hash);
  }
  return `var(--system-colour-${(Math.abs(hash) % 46) + 1})`;
};

/**
 * Whether a proposed tag list may be accepted by a tag editor.
 *
 * Only GROWTH past the cap is refused. A flat `next.length > MAX_TAGS_PER_MAIL` also blocks
 * shrinking, which strands any mail already over the cap — `tags` was unbounded before the limit
 * existed, so those mails are in the data and their owners could not remove a single chip.
 */
export const isTagListAcceptable = (next: readonly string[], current: readonly string[]): boolean =>
  next.length <= MAX_TAGS_PER_MAIL || next.length <= current.length;

/**
 * Caps the tag suggestion dropdown's height. MUI's listbox is not virtualised, so a project with a
 * large tag vocabulary would otherwise render a list taller than the viewport.
 * Note this REPLACES the AutoComplete wrapper's own `slotProps` default rather than merging with it,
 * so the wrapper's `padding: 0` is restated here.
 */
export const TAG_LISTBOX_SLOT_PROPS = {
  listbox: { sx: { padding: 0, minHeight: "8vh", maxHeight: "20vh" } },
} as const;

export const mailboxDefaultViews = (t?: any) => {
  if (!t) {
    t = (args: string) => args;
  }
  const groupBySubject = [
    { column: "subject", direction: "asc", preserveOrder: true },
  ];
  return [
    {
      isShared: false,
      resourceType: "MAILBOX",
      name: t("All Mails"),
      code: "ALL_MAILS",
      data: {
        groupConditions: groupBySubject,
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: "MAILBOX",
      name: t("Inbox"),
      code: "INBOX",
      data: {
        groupConditions: groupBySubject,
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: "MAILBOX",
      name: t("Sent"),
      code: "SENT",
      data: {
        groupConditions: groupBySubject,
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: "MAILBOX",
      name: t("Externally Forwarded"),
      code: "EXTERNAL",
      data: {
        groupConditions: groupBySubject,
      },
      isDefault: true,
    },
  ];
};

export const projectLayoutDefaultViews = (
  t?: (key: string) => string,
  customModuleNames: string[] = [],
) => {
  if (!t) {
    t = (args: string) => args;
  }
  // Module ids are sourced from `defaultModuleNamesMapper` (keyed by the `defaultModulesMapper`
  // flags) so these default-layout seeds, the dashboard `modulesMapper`, and `useStickySidebar` all
  // read the same strings — one source of truth for the ids persisted into layoutView `data.modules`.
  // A rename therefore happens in exactly one place, so the copies can't drift and silently drop a module.
  const COMMON_DATA_ENVIRONMENT = [
    defaultModuleNamesMapper[defaultModulesMapper.SPACES],
    defaultModuleNamesMapper[defaultModulesMapper.TRANSMITTALS],
    defaultModuleNamesMapper[defaultModulesMapper.ISSUES],
    defaultModuleNamesMapper[defaultModulesMapper.TASKS],
    defaultModuleNamesMapper[defaultModulesMapper.REVIEWS],
  ];
  const PROJECT_MANAGEMENT = [
    defaultModuleNamesMapper[defaultModulesMapper.MAILBOX],
    defaultModuleNamesMapper[defaultModulesMapper.FILES],
    defaultModuleNamesMapper[defaultModulesMapper.ESTIMATES],
    defaultModuleNamesMapper[defaultModulesMapper.SCHEDULES],
    defaultModuleNamesMapper[defaultModulesMapper.CONTRACTS],
    defaultModuleNamesMapper[defaultModulesMapper.INSPECTION],
    defaultModuleNamesMapper[defaultModulesMapper.PROGRESS_UPDATES],
    defaultModuleNamesMapper[defaultModulesMapper.BILLS],
    defaultModuleNamesMapper[defaultModulesMapper.CHANGE_ORDER],
    defaultModuleNamesMapper[defaultModulesMapper.PHOTOS],
  ];
  const DIGITAL_TWIN = [defaultModuleNamesMapper[defaultModulesMapper.ASSETS]];
  return [
    {
      isShared: false,
      resourceType: PROJECT_DASHBOARD_RESOURCE_TYPE,
      name: t("Common Data Environment"),
      code: "COMMON_DATA_ENVIRONMENT",
      data: {
        modules: COMMON_DATA_ENVIRONMENT,
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: PROJECT_DASHBOARD_RESOURCE_TYPE,
      name: t("Project Management"),
      code: "PROJECT_MANAGEMENT",
      data: {
        modules: PROJECT_MANAGEMENT,
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: PROJECT_DASHBOARD_RESOURCE_TYPE,
      name: t("Digital Twin"),
      code: "DIGITAL_TWIN",
      data: {
        modules: DIGITAL_TWIN,
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: PROJECT_DASHBOARD_RESOURCE_TYPE,
      name: t("All Modules"),
      code: ALL_MODULES_LAYOUT_CODE,
      data: {
        modules: [
          ...new Set([
            ...COMMON_DATA_ENVIRONMENT,
            ...DIGITAL_TWIN,
            ...PROJECT_MANAGEMENT,
            defaultModuleNamesMapper[defaultModulesMapper.CLASH],
            ...customModuleNames,
          ]),
        ],
      },
      isDefault: true,
    },
  ];
};

export const ALL_MODULES_LAYOUT_CODE = "ALL_MODULES";
export const LAYOUTS_SIDEBAR_CELL_ID = "Layouts";
// data-attribute stamped on the Layouts sidebar cell so the flyout's outside-click handler can tell a
// click on its own toggle apart from a genuine outside click. Shared by StickySidebar + ProjectLayouts.
export const SIDEBAR_LAYOUTS_TOGGLE_ATTR = "data-sidebar-layouts-toggle";

export const LAYOUT_ACTIVITY_TYPE = {
  CREATE: "create_layout",
  UPDATE: "update_layout",
  DELETE: "delete_layout",
};

export const LAYOUT_MENU_ACTION = {
  EDIT: "edit",
  RENAME: "rename",
  DELETE: "delete",
  DUPLICATE: "duplicate",
  MOVE_TO_SHARED: "moveToShared",
  DUPLICATE_AS_MY: "duplicateAsMy",
};

export const LAYOUT_ERROR_CODE = {
  /** A shared layout other users still have applied — deleting it would break their sidebar. */
  IN_USE_BY_OTHERS: "LAYOUT_IN_USE_BY_OTHERS",
} as const;

export const viewerDefaultViews = (t?: any) => {
  if (!t) {
    t = (args: string) => args;
  }
  return [
    {
      isDefault: true,
      resourceType: "VIEWER",
      name: t("Default Layout"),
      code: "DEFAULT_LAYOUT",
      isShared: false,
    },
  ];
};

export const issuesDefaultView = (t?: any) => {
  if (!t) {
    t = (args: string) => args;
  }
  return [
    {
      isShared: false,
      resourceType: "ISSUES",
      code: "CREATED_BY_ME",
      name: t("Created by me"),
      data: {
        queryConditions: {
          createdByName: {
            field: "createdByName",
            operator: "equal",
            value: "CURRENT_USER",
          },
        },
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: "ISSUES",
      name: t("Assigned to me"),
      code: "ASSIGNED_TO_ME",
      data: {
        queryConditions: {
          assignedToUserName: {
            field: "assignedToUserName",
            operator: "equal",
            value: "CURRENT_USER",
          },
        },
      },
      isDefault: true,
    },

    {
      isShared: false,
      resourceType: "ISSUES",
      data: {
        enableSorting: true,
        sortConditions: [],
      },
      name: t("All Issues"),
      code: "DEFAULT_VIEW",
      isDefault: true,
    },
  ];
};

export const tasksDefaultView = (t: (key: string) => string = identityTranslate) => {
  return [
    {
      isShared: false,
      resourceType: "TASKS",
      data: {
        enableSorting: true,
        sortConditions: [],
      },
      name: t("All Tasks"),
      code: "DEFAULT_VIEW",
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: "TASKS",
      name: t("Assigned to Me"),
      code: "ASSIGNED_TO_ME",
      data: {
        queryConditions: {
          taskAssignedTo: {
            field: "taskAssignedTo",
            operator: "equal",
            value: "CURRENT_USER",
          },
        },
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: "TASKS",
      name: t("Grouped By Assignee"),
      code: "GROUPED_BY_ASSIGNEE",
      data: {
        enableGrouping: true,
        groupConditions: [{ column: "taskAssignedTo", direction: "asc", preserveOrder: true }],
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: "TASKS",
      name: t("Grouped By Status"),
      code: "GROUPED_BY_STATUS",
      data: {
        enableGrouping: true,
        groupConditions: [{ column: "status", direction: "asc", preserveOrder: true }],
      },
      isDefault: true,
    },
  ];
};

export const REVIEW_LIST_GRID_TYPE = "REVIEW_LIST";

export const reviewsDefaultView = (t?: any) => {
  if (!t) {
    t = (args: string) => args;
  }
  return [
    {
      isShared: false,
      resourceType: "REVIEWS",
      gridType: REVIEW_LIST_GRID_TYPE as GridType,
      data: {
        enableSorting: true,
        sortConditions: [],
      },
      name: t("All Reviews"),
      code: "DEFAULT_VIEW",
      isDefault: true,
    },
  ];
};

export const REVIEW_FLOWS_GRID_TYPE = "REVIEW_FLOWS";

export const getReviewFlowsViewSet = (resourceType: string, t: (key: string) => string = identityTranslate) => {
  return [
    {
      isShared: false,
      resourceType,
      gridType: REVIEW_FLOWS_GRID_TYPE as GridType,
      isDefault: true,
      code: GROUPED_BY_SPACE_VIEW_CODE,
      name: t("Grouped by Spaces"),
      data: { groupConditions: [{ column: "space.spaceName", direction: "asc" }] },
    },
    {
      isShared: false,
      resourceType,
      gridType: REVIEW_FLOWS_GRID_TYPE as GridType,
      isDefault: true,
      code: "CREATED_BY_ME",
      name: t("Created by Me"),
      data: { queryConditions: { where: [{ field: "createdById", operator: "equal", value: "CURRENT_USER" }] } },
    },
  ];
};

export const getReviewFlowsSelectedLayoutKey = (resourceType: string) =>
  `REVIEW_FLOWS_${resourceType}_SELECTED_LAYOUT`;

export const reviewFlowsDefaultViews = (t?: any) => getReviewFlowsViewSet("REVIEWS", t);

export const GRID_TYPES = [
  FORM_LIST_GRID_TYPE,
  FORM_FLOWS_GRID_TYPE,
  FORM_ENTRIES_GRID_TYPE,
  REVIEW_LIST_GRID_TYPE,
  REVIEW_FLOWS_GRID_TYPE,
] as const;

export type GridType = (typeof GRID_TYPES)[number];

const defaultViewsMapByGridType: Record<GridType, (resourceType: string, t: (key: string) => string) => any[]> = {
  [FORM_LIST_GRID_TYPE]: (resourceType, t) => getFormsViewSet(resourceType, t),
  [FORM_FLOWS_GRID_TYPE]: (resourceType, t) => getFormFlowsViewSet(resourceType, t),
  [REVIEW_LIST_GRID_TYPE]: (_resourceType, t) => reviewsDefaultView(t),
  [REVIEW_FLOWS_GRID_TYPE]: (resourceType, t) => getReviewFlowsViewSet(resourceType, t),
  [FORM_ENTRIES_GRID_TYPE]: (_resourceType, t) => formEntriesDefaultViews("", t),
};

export const projectsDefaultView = (t?: any) => {
  if (!t) {
    t = (args: string) => args;
  }
  return [
    {
      isShared: false,
      resourceType: "PROJECTS",
      data: {
        enableSorting: true,
        sortConditions: [],
      },
      name: t("All Projects"),
      code: "DEFAULT_VIEW",
      isDefault: true,
    },
  ];
};

export const GANTT_VIEW_CODES = {
  ALL_ACTIVITIES: "ALL_ACTIVITIES",
  ALL_ACTIVITIES_PLANNED: "ALL_ACTIVITIES_PLANNED",
  ALL_ACTIVITIES_ACTUAL: "ALL_ACTIVITIES_ACTUAL",
  ACTIVITIES_GROUPED_BY_RESOURCES: "ACTIVITIES_GROUPED_BY_RESOURCES",
} as const;

export const GANTT_GROUP_BY_RESOURCE = "resource";

export const GANTT_SCHEDULE_RESOURCE_TYPE = "GANTT_SCHEDULE";
export const GANTT_SCHEDULE_SELECTED_LAYOUT_KEY =
  "GANTT_SCHEDULE_SELECTED_LAYOUT";

export const ganttDefaultViews = (t?: (key: string) => string) => {
  const translate = t ?? ((args: string) => args);
  const baseColumns = [
    "TaskDisp",
    "text",
    "start_date",
    "end_date",
    "duration",
  ];
  return [
    {
      isShared: false,
      resourceType: GANTT_SCHEDULE_RESOURCE_TYPE,
      name: translate("All Activities"),
      code: GANTT_VIEW_CODES.ALL_ACTIVITIES,
      data: {
        visibleColumns: baseColumns,
        groupBy: null,
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: GANTT_SCHEDULE_RESOURCE_TYPE,
      name: translate("All Activities with Planned Columns"),
      code: GANTT_VIEW_CODES.ALL_ACTIVITIES_PLANNED,
      data: {
        visibleColumns: [
          ...baseColumns,
          "plannedStartDate",
          "plannedEndDate",
          "plannedDuration",
          "planned%",
        ],
        groupBy: null,
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: GANTT_SCHEDULE_RESOURCE_TYPE,
      name: translate("All Activities with Actual Columns"),
      code: GANTT_VIEW_CODES.ALL_ACTIVITIES_ACTUAL,
      data: {
        visibleColumns: [...baseColumns, "actualStartDate", "actualEndDate"],
        groupBy: null,
      },
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: GANTT_SCHEDULE_RESOURCE_TYPE,
      name: translate("Activities Grouped by Resources"),
      code: GANTT_VIEW_CODES.ACTIVITIES_GROUPED_BY_RESOURCES,
      data: {
        visibleColumns: baseColumns,
        groupBy: GANTT_GROUP_BY_RESOURCE,
      },
      isDefault: true,
    },
  ];
};

export const contractBoqDefaultViews = (t?: (key: string) => string) => {
  if (!t) {
    t = (args: string) => args;
  }
  const progressViewColumns = [
    "budget_quantity_detail",
    "serial",
    "code",
    "HSN_SAC",
    "description",
    "rate",
    "purchaseUnit.name",
    "originalQuantity",
    "originalAmountInPercentage",
    "changeOrderQuantity",
    "changeOrderAmountInPercentage",
    "revisedQuantity",
    "revisedAmountInPercentage",
    "usedQuantity",
    "usedAmountInPercentage",
    "remainingQuantity",
    "remainingAmountInPercentage",
    "inspectedQuantity",
    "inspectedAmountInPercentage",
    "billedQuantity",
    "billedAmountInPercentage",
  ];
  return [
    {
      isShared: false,
      resourceType: "CONTRACT_BOQ",
      data: {
        enableSorting: true,
        sortConditions: [],
      },
      name: t("Default View"),
      code: "DEFAULT_VIEW",
      isDefault: true,
    },
    {
      isShared: false,
      resourceType: "CONTRACT_BOQ",
      isDefault: true,
      data: { columns: progressViewColumns },
      name: t("Progress View"),
      code: "PROGRESS_VIEW",
    },
  ];
};
export const getDefaultViewDisplayName = (item: any, t: any) => {
  if (!t) t = (args: string) => args;
  if (!item?.isDefault) return item?.name;

  const getGridViews =
    typeof item.gridType === "string" ? defaultViewsMapByGridType[item.gridType as GridType] : undefined;
  if (getGridViews) {
    const gridViews = getGridViews(item.resourceType, t);
    const gridDefaultView = gridViews.find((view: any) => view.code === item.code);
    return gridDefaultView ? gridDefaultView.name : item?.name;
  }

  const defaultViewsMap: any = {
    TRANSMITTALS: () => transmittalsDefaultViews(t),
    FILES: () => filesDefaultViews(t),
    MAILBOX: () => mailboxDefaultViews(t),
    VIEWER: () => viewerDefaultViews(t),
    INSPECTION: () => formsDefaultViews(t),
    PROGRESS_UPDATES: () => formsDefaultViews(t),
    BILLS: () => formsDefaultViews(t),
    CHANGE_ORDER: () => formsDefaultViews(t),
    PAYMENTS: () => formsDefaultViews(t),
    JIRA: () => jiraDefaultViews(t),
    ISSUES: () => issuesDefaultView(t),
    REVIEWS: () => reviewsDefaultView(t),
    PROJECTS: () => projectsDefaultView(t),
    GANTT_SCHEDULE: () => ganttDefaultViews(t),
    CONTRACT_BOQ: () => contractBoqDefaultViews(t),
    TASKS: () => tasksDefaultView(t),
  };

  const getViews = defaultViewsMap[item.resourceType];
  if (!getViews) return item?.name;

  const defaultViews = getViews();
  const defaultView = defaultViews.find((view: any) => view.code === item.code);
  return defaultView ? defaultView.name : item.name;
};

export const getPermissionIdDescriptionMapper = (t: any) => {
  const permissions: any = getPermissions(t);
  const obj: any = {};

  Object.keys(permissions).forEach((key) => {
    const permission = permissions[key];
    obj[permission.id] = permission.description;
  });
  return obj;
};

export const getPermissionIdKeyMapper = () => {
  const permissions: any = getPermissions();
  const obj: any = {};

  Object.keys(permissions).forEach((key) => {
    const permission = permissions[key];
    obj[permission.id] = key;
  });
  return obj;
};

export const getPermissionIdGroupIdMapper = () => {
  const permissions: any = getPermissions();
  const obj: Record<number, number> = {};

  Object.keys(permissions).forEach((key) => {
    const permission = permissions[key];
    if (permission.groupId !== undefined) {
      obj[permission.id] = permission.groupId;
    }
  });
  return obj;
};

export const groupPermissionsByGroupKey = (
  rolePermissions: any[],
  resourceType: string,
  t?: any,
) => {
  const groups = getPermissionGroups(t).filter((g) => g.type === resourceType);
  const idToGroupId = getPermissionIdGroupIdMapper();
  const descriptionMapper = getPermissionIdDescriptionMapper(t);
  const groupIdToKey: Record<number, string> = {};
  const data: Record<string, any[]> = {};

  groups.forEach((g) => {
    data[g.key] = [];
    groupIdToKey[g.id] = g.key;
  });

  const fallbackGroup = groups.find((g) => g.key === "general");
  const fallbackKey = fallbackGroup?.key;

  rolePermissions.forEach((permission: any) => {
    const groupId = idToGroupId[permission.id];
    const key = groupId !== undefined ? groupIdToKey[groupId] : fallbackKey;
    if (key && data[key]) {
      data[key].push({
        ...permission,
        description: descriptionMapper[permission.id] ?? permission.description,
      });
    }
  });

  return data;
};

const setDeep = (obj: any, path: any, value: any) => {
  // sonar-disable-next-line
  const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".");
  const lastIndex = parts.length - 1;
  let current = obj;

  parts.forEach((part: string, index: number) => {
    if (index === lastIndex) {
      current[part] = parseValue(value);
      return;
    }

    current[part] = current[part] || (isNumeric(parts[index + 1]) ? [] : {});
    current = current[part];
  });
};

const parseValue = (value: any) => {
  const stringValue = String(value);
  if (stringValue === "null") return null;
  if (stringValue === "true") return true;
  if (stringValue === "false") return false;
  if (
    !Number.isNaN(Number(value)) &&
    value !== "" &&
    typeof value === "string"
  ) {
    return Number(value);
  }
  return value;
};

const isNumeric = (str: string) => /^\d+$/.test(str);

export const convertQueryToPostPayload = (req: any) => {
  const query: any = req.query;
  const result = {};
  for (const key in query) {
    setDeep(result, key, query[key]);
  }
  req.body = result;
  req.params = result;
  return result;
};

export const SECTION_TYPE_REFERENCE = "SECTION_TYPE_REFERENCE";
export const SECTION_TYPE_FORM = "SECTION_TYPE_FORM";
export const SECTION_TYPE_TASK = "SECTION_TYPE_TASK";
export const SECTION_TYPE_MILESTONE = "SECTION_TYPE_MILESTONE";
export const SECTION_TYPE_QUANTITY = "SECTION_TYPE_QUANTITY";
export const SECTION_TYPE_TABLE_SECTION = "SECTION_TYPE_TABLE_SECTION";
export const SECTION_TYPE_WBS_ITEMS = "SECTION_TYPE_WBS_ITEMS";
export const SECTION_TYPE_SUMMARY_OF_CHANGES =
  "SECTION_TYPE_SUMMARY_OF_CHANGES";
export const SECTION_TYPE_REVIEWS = "SECTION_TYPE_REVIEWS";
export const SECTION_TYPE_INSPECTION = "SECTION_TYPE_INSPECTION";
export const SECTION_TYPE_PROGRESS_UPDATES = "SECTION_TYPE_PROGRESS_UPDATES";
export const SECTION_TYPE_BILLS = "SECTION_TYPE_BILLS";
export const SECTION_TYPE_CHANGE_ORDERS = "SECTION_TYPE_CHANGE_ORDERS";
export const SECTION_TYPE_BLANK_SECTION = "SECTION_TYPE_BLANK_SECTION";
export const SECTION_TYPE_PRESET_SECTION = "SECTION_TYPE_PRESET_SECTION";
export const SECTION_TYPE_SECTION_FROM_LIBRARY =
  "SECTION_TYPE_SECTION_FROM_LIBRARY";
export const SECTION_TYPE_CUSTOM_MODULES = "SECTION_TYPE_CUSTOM_MODULES";

export const debounceDelay = 300;

export const formCategories = {
  inspection: "INSPECTION",
  progressUpdates: "PROGRESS_UPDATES",
  changeOrder: "CHANGE_ORDER",
  payments: "PAYMENTS",
  mailBox: "MAILBOX",
  bills: "BILLS",
  issues: "ISSUES",
  projects: "PROJECTS",
  files: "FILES",
  contract: "CONTRACT",
  model: "MODEL",
  reviews: "REVIEWS",
  transmittal: "TRANSMITTAL",
  assets: "ASSETS",
  assetsInstance: "ASSETS_INSTANCE",
  drawing: "DRAWING",
  tasks: "TASKS",
};

export const DEFAULT_TEMPLATE_RESOURCE_TYPES = [formCategories.mailBox, formCategories.issues, formCategories.tasks];

export const formNotificationTypeLabels = {
  inspections: "Inspections",
  progressUpdates: "Progress Updates",
  bills: "Bills",
  changeOrders: "Change Orders",
} as const;

export const getFormNotificationType = (resourceType: string): string => {
  switch (resourceType) {
    case formCategories.inspection:
      return formNotificationTypeLabels.inspections;
    case formCategories.progressUpdates:
      return formNotificationTypeLabels.progressUpdates;
    case formCategories.bills:
      return formNotificationTypeLabels.bills;
    case formCategories.changeOrder:
      return formNotificationTypeLabels.changeOrders;
    default:
      return resourceType;
  }
};

export const presetSectionsCodeObject: any = {
  SECTION_TYPE_QUANTITY,
  SECTION_TYPE_REFERENCE,
  SECTION_TYPE_FORM,
  SECTION_TYPE_TASK,
  SECTION_TYPE_MILESTONE,
  SECTION_TYPE_TABLE_SECTION,
  SECTION_TYPE_WBS_ITEMS,
  SECTION_TYPE_SUMMARY_OF_CHANGES,
  SECTION_TYPE_REVIEWS,
  SECTION_TYPE_INSPECTION,
  SECTION_TYPE_PROGRESS_UPDATES,
  SECTION_TYPE_BILLS,
  SECTION_TYPE_CHANGE_ORDERS,
};

/**
 * A table section is a grid: `questionFields` are its columns and `tableDataSource` its rows. A
 * section that declares itself a table must carry BOTH keys — a grid with no column list, or no row
 * container, is malformed rather than empty. EMPTY ARRAYS STAY LEGITIMATE and must keep passing:
 * the template builder mints a new table section as `{ questionFields: [], tableDataSource: [] }`
 * (formTemplate.tsx "add-table-section"), and a table whose filler has not added a row yet keeps
 * `tableDataSource: []`. Only an absent key is malformed.
 *
 * The rule lives here, beside SECTION_TYPE_TABLE_SECTION, because four validators used to re-derive
 * it independently and inconsistently: web `addValidationToForms` / `checkMandatoryFields` read both
 * arrays through `!` (TypeError on a malformed section), the server's `checkAllMandatoryFields` read
 * them through `?.` (silently skipped it), and the unused `addValidationToInputFields` a third way.
 * The same document therefore crashed one caller and was waved through by another. Callers should
 * import this pair instead of re-testing the fields inline.
 */
export const isTableSection = (section: {
  // `| undefined` as well as optional: every caller hands this a section whose `sectionCode` is the
  // schema-inferred `string | undefined`, which a bare `?:` may not hold under
  // `exactOptionalPropertyTypes`. Absent, `undefined` and `null` all fail the comparison below
  // identically, so this widens the declaration to what the call sites already pass.
  sectionCode?: string | null | undefined;
}): boolean => section.sectionCode === SECTION_TYPE_TABLE_SECTION;

/** The two members a table section must carry, before either has been proven present. */
type MaybeTableSection = {
  questionFields?: unknown;
  tableDataSource?: unknown;
};

/**
 * Narrows a section already known to be a table section (see `isTableSection`) to one whose grid is
 * addressable. Generic over the caller's own section type so it works for all three shapes in play —
 * the wire `FormTemplateField`, the stored `InstanceTemplateSection` and web's `FormInstanceSection`
 * — narrowing each to its own element type rather than a shared lowest common denominator.
 */
export const isWellFormedTableSection = <S extends MaybeTableSection>(
  section: S,
): section is S & {
  questionFields: NonNullable<S["questionFields"]>;
  tableDataSource: NonNullable<S["tableDataSource"]>;
} =>
  Array.isArray(section.questionFields) &&
  Array.isArray(section.tableDataSource);

export const libraryEntities = {
  FORM_FLOW: "FORM_FLOW",
  TEMPLATE_SECTION: "TEMPLATE_SECTION",
};
export const getProjectMailThreadsPageUrl = (
  projectId: string,
  mailId: string,
) => `/project/${projectId}/mails/${mailId}/mailThread?projectId=${projectId}`;

export function getDistanceInMeters({ pos1, pos2 }: any) {
  if (!pos1 || !pos2) {
    return 0;
  }
  const toRad = (deg: any) => (deg * Math.PI) / 180;

  const R = 6371000; // Radius of Earth in meters
  const lat1 = toRad(pos1.lat);
  const lat2 = toRad(pos2.lat);
  const deltaLat = toRad(pos2.lat - pos1.lat);
  const deltaLng = toRad(pos2.lng - pos1.lng);

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLng / 2) *
      Math.sin(deltaLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}
export const recordStatus = {
  ACTIVE: 0,
  ARCHIVED: 1,
  DELETED: 2,
} as const;

export const projectSpaceActiveStatusCheck = {
  "project.recordStatus": recordStatus.ACTIVE,
  "space.recordStatus": recordStatus.ACTIVE,
} as const;

export const clashVisibilityOptions: any = {
  XRAY_MODE: "XRAY_MODE",
  HIDE_NON_CLASHING: "HIDE_NON_CLASHING",
  SHOWALL: "SHOWALL",
};

export const cubeParams = [
  "CUBE Name",
  "CUBE Volume",
  "CUBE Area",
  "Quantity",
  "Unit",
];

export const getZohoBackendUrl = () => process.env.REACT_APP_ZOHO_BACKEND_URL;

export const streamingModeMap = {
  All: "All",
  Interactive: "Interactive",
  OnDemand: "On Demand",
};

export const objectTreeSupportedExtension = [
  "rvt",
  "nwd",
  "ifc",
  "ifczip",
  "rfa",
];

export const selectionSetSupportedExtension = [
  "rvt",
  "nwd",
  "ifc",
  "ifczip",
  "rfa",
  "dwg",
  "fbx",
];

export const hasInvalidChars = (name: string) => {
  const trimmed = name.trim();

  const hasInvalidSymbols = /[\\/:*?"<>|]/.test(trimmed);
  const endsWithDotOrSpace = /[.\s]$/.test(name); // use original name for space check

  return hasInvalidSymbols || endsWithDotOrSpace;
};

export const RC_MENU_ITEMS = {
  ISOLATE: "Isolate",
  FOCUS: "Focus",
  HIDE: "Hide",
  ON_DEMAND_LOAD: "onDemandLoad",
  OVERRIDE: "Override",
  TRANSFORM: "Transform",
  SCALE: "Scale",
  COLOR: "Color",
  SET_COLOR: "Set Color",
  MODIFY_COLOR: "Modify Color",
  TRANSPARENCY: "Transparency",
  SELECTION: "Selection",
  RESET: "Reset",
  FILE: "File",
  ELEMENT: "Element",
  SHOW_ALL: "Show All",
  PROPERTIES: "Properties",
  PARENT: "Parent",
  GRAND_PARENT: "Grand Parent",
};
export const preExistingModules = [
  "Inspections",
  "Bills",
  "Progress Updates",
  "General Forms",
  "Permit To Work",
  "Change Orders",
  "Estimates",
  "Schedules",
  "Files",
  "Transmittals",
  "Tasks",
  "Spaces",
  "Reviews",
  "Issues",
  "Mailbox",
  "Clash Tests",
  "Contracts",
  "Risk Management",
  "Payments",
  "Tenders",
  "Asset Register",
  "Procurement",
  "Projects",
  "Model",
];
export const PROJECT = "project";
export const DEFAULT_CALENDAR_NAME = "Default CUBE Calendar";
export const PROJECT_CALENDAR = "PROJECTCALENDAR";

export const portalRoleRanks: PortalRoleRanks = {
  ...defaultPortalRoleRanks,
  CUSTOM_PORTAL_ROLE: CUSTOM_ROLE_RANK,
};
export const projectRoleRanks: ProjectRoleRanks = {
  ...defaultProjectRoleRanks,
  CUSTOM_PROJECT_ROLE: CUSTOM_ROLE_RANK,
};

export const PORTAL_FULL_SEAT = 1;
export const PORTAL_NO_SEAT = 0;
export const PORTAL_HALF_SEAT = 2;

export const COMPANY_GST_STATE_CODE = "HR";

export const COMPANY_COUNTRY = "India";

export const isInterStateGst = (country?: string, stateCode?: string) =>
  country === COMPANY_COUNTRY && !!stateCode && stateCode !== COMPANY_GST_STATE_CODE;

export const CDEModulesEnabled = [
  "Spaces",
  "Tasks",
  "Transmittals",
  "Issues",
  "Reviews",
  "Meetings",
];
export const PMModulesEnabled = [
  "Files",
  "Mailbox",
  "Estimates",
  "Schedules",
  "Progress Updates",
  "Bills",
  "Change Orders",
  "Inspections",
  "Contracts",
  "Simulation",
  "BIM Takeoff",
  "Rate Analysis",
];
export const DTModulesEnabled = ["Assets"];

export const CLASH_ADDON_CODE = "CD";
export const CUSTOM_MODULE_ADDON_CODE = "CM";
export const BIM_TAKEOFF_ADDON_CODE = "BT";
export const RATE_ANALYSIS_ADDON_CODE = "RA";
export const SIMULATION_ADDON_CODE = "4D5D";

export const ADDON_CODE_TO_LAYOUT_FLAG = {
  [BIM_TAKEOFF_ADDON_CODE]: "BIM_TAKEOFF.ENABLE",
  [RATE_ANALYSIS_ADDON_CODE]: "RATE_ANALYSIS.ENABLE",
  [SIMULATION_ADDON_CODE]: "SIMULATION.ENABLE",
} as const satisfies Record<string, string>;

export const ADDONS_DISPLAY_NAME = {
  [CLASH_ADDON_CODE]: "Clash Tests",
  [CUSTOM_MODULE_ADDON_CODE]: "Custom Modules",
  [BIM_TAKEOFF_ADDON_CODE]: "BIM Takeoff",
  [RATE_ANALYSIS_ADDON_CODE]: "Rate Analysis",
  [SIMULATION_ADDON_CODE]: "Simulation",
} as const;

export type AddonsDisplayNameType = keyof typeof ADDONS_DISPLAY_NAME;

export const MATERIAL = "material";
export const P6TYPE = "primaveraP6-parse";

export const getIsAddonAccess = (addonCode: string, portalData: any) => {
  const addons = _.get(portalData, "subscription.addons");
  return (
    addons == undefined ||
    addons.some((addon: any) => addon.addon_code.includes(addonCode))
  );
};

export const fileConversionStatus = {
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  DOWNLOADED: "DOWNLOADED",
  ERRORED: "ERRORED",
  RETRY: "RETRY",
  NOT_REQUIRED: "NOT_REQUIRED",
};

export const exportType = {
  PDF: "PDF",
  EXCEL: "EXCEL",
  P6: "primaveraP6",
  MSP: "msproject",
  SIMULATION_VIDEO: "simulationVideo",
};

export const DOCUMENT_TYPE = {
  DOCUMENT_TYPE_MODEL_PROPERTIES: "MODEL_PROPERTIES",
  DOCUMENT_TYPE_DRAWING_PROPERTIES: "DRAWING_PROPERTIES",
  DOCUMENT_TYPE_CUSTOM_PROPERTIES: "CUSTOM_PROPERTIES",
  DOCUMENT_TYPE_QUANTITY: "QUANTITY",
  DOCUMENT_TYPE_ASSETDATA: "ASSETDATA",
};
export const defaultModulesMapper: any = {
  INSPECTION: "INSPECTION",
  PROGRESS_UPDATES: "PROGRESS_UPDATES",
  BILLS: "BILLS",
  CHANGE_ORDER: "CHANGE_ORDER",
  CONTRACTS: "CONTRACTS",
  SCHEDULES: "SCHEDULES",
  ESTIMATES: "ESTIMATES",
  MAILBOX: "MAILBOX",
  FILES: "FILES",
  CLASH: "CLASH_TEST",
  REVIEWS: "REVIEWS",
  TRANSMITTALS: "TRANSMITTALS",
  ISSUES: "ISSUES",
  TASKS: "TASKS",
  FOLDERS: "FOLDERS",
  SPACES: "SPACES",
  ASSETS: "ASSETS",
  PHOTOS: "PHOTOS",
};

/**
 * Maps each built-in module flag (`defaultModulesMapper.*`) to its display name. This is also the
 * single source of truth for the module ids persisted into layoutView `data.modules`: the
 * default-layout seeds (`projectLayoutDefaultViews`), the dashboard `modulesMapper`, and
 * `useStickySidebar` all derive their ids from these values, so the copies can't drift apart.
 * The values must therefore stay in sync with existing `data.modules` rows — do NOT rename one
 * without migrating that data. (`Inspections`/`Change Orders` are intentionally plural to match the
 * ids already stored and the labels shown across the dashboard/sidebar.)
 */
export const defaultModuleNamesMapper: Record<string, string> = {
  [defaultModulesMapper.INSPECTION]: "Inspections",
  [defaultModulesMapper.PROGRESS_UPDATES]: "Progress Updates",
  [defaultModulesMapper.BILLS]: "Bills",
  [defaultModulesMapper.CHANGE_ORDER]: "Change Orders",
  [defaultModulesMapper.CONTRACTS]: "Contracts",
  [defaultModulesMapper.SCHEDULES]: "Schedules",
  [defaultModulesMapper.ESTIMATES]: "Estimates",
  [defaultModulesMapper.MAILBOX]: "Mailbox",
  [defaultModulesMapper.FILES]: "Files",
  [defaultModulesMapper.CLASH]: "Clash Tests",
  [defaultModulesMapper.REVIEWS]: "Reviews",
  [defaultModulesMapper.TRANSMITTALS]: "Transmittals",
  [defaultModulesMapper.ISSUES]: "Issues",
  [defaultModulesMapper.TASKS]: "Tasks",
  [defaultModulesMapper.FOLDERS]: "Folders",
  [defaultModulesMapper.SPACES]: "Spaces",
  [defaultModulesMapper.ASSETS]: "Assets",
  [defaultModulesMapper.PHOTOS]: "Photos",
};

const resourceRoles = folderRoles();
const fullAccessObj = {
  canCreate: true,
  role: resourceRoles.RESOURCE_ADMIN.id,
  permissions: resourceRoles.RESOURCE_ADMIN.permissionIds,
};
const canEditObj = {
  canCreate: true,
  role: resourceRoles.RESOURCE_EDITOR.id,
  permissions: resourceRoles.RESOURCE_EDITOR.permissionIds,
};
export const canViewObj = {
  canCreate: false,
  role: resourceRoles.RESOURCE_VIEWER.id,
  permissions: resourceRoles.RESOURCE_VIEWER.permissionIds,
};
export const canDownloadObj = {
  canCreate: true,
  role: resourceRoles.RESOURCE_DOWNLOADER.id,
  permissions: resourceRoles.RESOURCE_DOWNLOADER.permissionIds,
};
export const noAccessObj = {
  canCreate: false,
  role: resourceRoles.RESOURCE_NO_ACCESS.id,
  permissions: resourceRoles.RESOURCE_NO_ACCESS.permissionIds,
};
export const noAccessPermissions: any = {
  FOLDERS: {
    ...noAccessObj,
  },
  FILES: {
    ...noAccessObj,
  },
  TASKS: {
    ...noAccessObj,
  },
  ISSUES: {
    ...noAccessObj,
  },
  CONTRACTS: {
    ...noAccessObj,
  },
  ESTIMATES: {
    ...noAccessObj,
  },
  SCHEDULES: {
    ...noAccessObj,
  },
  REVIEWS: {
    ...noAccessObj,
  },
  CLASH_TEST: {
    ...noAccessObj,
  },
  TRANSMITTALS: {
    ...noAccessObj,
  },
  INSPECTION: {
    ...noAccessObj,
  },
  PROGRESS_UPDATES: {
    ...noAccessObj,
  },
  BILLS: {
    ...noAccessObj,
  },
  CHANGE_ORDER: {
    ...noAccessObj,
  },
  ASSETS: {
    ...noAccessObj,
  },
};
export const managerDefaultPermissions: any = {
  FOLDERS: {
    ...fullAccessObj,
  },
  FILES: {
    ...fullAccessObj,
  },
  TASKS: {
    ...fullAccessObj,
  },
  ISSUES: {
    ...fullAccessObj,
  },
  CONTRACTS: {
    ...fullAccessObj,
  },
  ESTIMATES: {
    ...fullAccessObj,
  },
  SCHEDULES: {
    ...fullAccessObj,
  },
  REVIEWS: {
    ...canViewObj,
    canCreate: true,
  },
  CLASH_TEST: {
    ...fullAccessObj,
  },
  TRANSMITTALS: {
    ...fullAccessObj,
  },
  INSPECTION: {
    ...canDownloadObj,
  },
  PROGRESS_UPDATES: {
    ...canDownloadObj,
  },
  BILLS: {
    ...canDownloadObj,
  },
  CHANGE_ORDER: {
    ...canDownloadObj,
  },
  ASSETS: {
    ...fullAccessObj,
  },
};
export const editorDefaultPermissions: any = {
  FOLDERS: {
    ...canEditObj,
  },
  FILES: {
    ...canEditObj,
  },
  TASKS: {
    ...noAccessObj,
    canCreate: true,
  },
  ISSUES: {
    ...noAccessObj,
    canCreate: true,
  },
  CONTRACTS: {
    ...canEditObj,
    canCreate: false,
  },
  ESTIMATES: {
    ...canEditObj,
    canCreate: false,
  },
  SCHEDULES: {
    ...canEditObj,
    canCreate: false,
  },
  REVIEWS: {
    ...noAccessObj,
    canCreate: true,
  },
  CLASH_TEST: {
    ...noAccessObj,
    canCreate: true,
  },
  TRANSMITTALS: {
    ...noAccessObj,
    canCreate: true,
  },
  INSPECTION: {
    ...noAccessObj,
    canCreate: true,
  },
  PROGRESS_UPDATES: {
    ...noAccessObj,
    canCreate: true,
  },
  BILLS: {
    ...noAccessObj,
    canCreate: true,
  },
  CHANGE_ORDER: {
    ...noAccessObj,
    canCreate: true,
  },
  ASSETS: {
    ...noAccessObj,
    canCreate: true,
  },
};
export const viewerDefaultPermissions: any = {
  ...noAccessPermissions,
  FOLDERS: {
    ...canViewObj,
  },
  FILES: {
    ...canViewObj,
  },
};

export function isMergeAllowed(fileObjArray: any[]) {
  let fileSettings = {
    mergedAllowed: true,
    isFile: true,
  };

  let hasEstimate = false;
  let hasFile = false;

  for (let i = 0; i < fileObjArray.length; i++) {
    const fileObj = fileObjArray[i];

    if (fileObj.resourceType === "RESOURCE_TYPE_MERGED_FILE") {
      if (hasEstimate) {
        fileSettings.mergedAllowed = false;
        break;
      }
      hasFile = true;
    }

    if (fileObj.resourceType === "RESOURCE_TYPE_ESTIMATE") {
      fileSettings.isFile = false;
      hasEstimate = true;
    } else {
      if (!["CAD", "BIM"].includes(fileObj.engineType)) {
        fileSettings.mergedAllowed = false;
        break;
      }
      hasFile = true;
    }
  }

  if (hasEstimate && hasFile) {
    fileSettings.mergedAllowed = false;
  }

  return fileSettings;
}
